import { API_BASE_URL } from '../../../api/config';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('WORDPRESS_ADAPTER');

export interface WordPressPost {
  id?: number;
  title: string;
  content: string;
  excerpt?: string;
  status?: 'draft' | 'publish' | 'pending' | 'private' | 'future';
  postType?: string;
  categories?: number[] | string[];
  tags?: number[] | string[];
  featuredMedia?: number;
  meta?: Record<string, any>;
  date?: string;
  slug?: string;
}

export interface WordPressMedia {
  id: number;
  source_url: string;
  title: string;
  mime_type: string;
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
}

export interface WordPressTag {
  id: number;
  name: string;
  slug: string;
}

export interface WordPressPublishResult {
  post_id: number;
  post_url: string;
  status: string;
  message: string;
  media_uploaded?: number[];
  categories_created?: string[];
  tags_created?: string[];
}

export class WordPressAdapter {
  private baseUrl = `${API_BASE_URL}/integrations/wordpress`;

  private getAuthHeaders() {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      throw new Error('No auth token found');
    }
    return {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Publish a post to WordPress
   */
  async publishPost(
    integrationId: string,
    post: WordPressPost
  ): Promise<WordPressPublishResult> {
    try {
      log.log('[WordPressAdapter] Publishing post:', {
        title: post.title.substring(0, 50),
        postType: post.postType || 'post',
        hasContent: !!post.content,
        contentLength: post.content?.length || 0
      });

      const response = await fetch(`${this.baseUrl}/publish`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          post: {
            title: post.title,
            content: post.content,
            excerpt: post.excerpt,
            status: post.status || 'publish',
            type: post.postType || 'post',
            categories: post.categories || [],
            tags: post.tags || [],
            featured_media: post.featuredMedia,
            meta: post.meta || {},
            date: post.date,
            slug: post.slug,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to publish post');
      }

      const data = await response.json();
      log.log('[WordPressAdapter] Post published successfully:', data.post_id);
      
      return {
        post_id: data.post_id,
        post_url: data.post_url || '',
        status: data.status || 'publish',
        message: data.message || 'Post published successfully',
        media_uploaded: data.media_uploaded || [],
        categories_created: data.categories_created || [],
        tags_created: data.tags_created || [],
      };
    } catch (error) {
      log.error('[WordPressAdapter] Error publishing post:', error);
      throw error;
    }
  }

  /**
   * Upload media file to WordPress
   */
  async uploadMedia(
    integrationId: string,
    fileUrl: string,
    filename: string,
    title?: string,
    altText?: string
  ): Promise<WordPressMedia> {
    try {
      log.log('[WordPressAdapter] Uploading media:', { filename, fileUrl: fileUrl.substring(0, 50) });

      const response = await fetch(`${this.baseUrl}/upload-media`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          file_url: fileUrl,
          filename,
          title: title || filename,
          alt_text: altText,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to upload media');
      }

      const data = await response.json();
      log.log('[WordPressAdapter] Media uploaded successfully:', data.id);
      
      return {
        id: data.id,
        source_url: data.source_url,
        title: data.title,
        mime_type: data.mime_type,
      };
    } catch (error) {
      log.error('[WordPressAdapter] Error uploading media:', error);
      throw error;
    }
  }

  /**
   * Get or create category
   */
  async getOrCreateCategory(
    integrationId: string,
    categoryName: string,
    parentId?: number
  ): Promise<WordPressCategory> {
    try {
      log.log('[WordPressAdapter] Getting or creating category:', categoryName);

      const response = await fetch(`${this.baseUrl}/category`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          name: categoryName,
          parent_id: parentId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to get or create category');
      }

      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
      };
    } catch (error) {
      log.error('[WordPressAdapter] Error getting or creating category:', error);
      throw error;
    }
  }

  /**
   * Get or create tag
   */
  async getOrCreateTag(
    integrationId: string,
    tagName: string
  ): Promise<WordPressTag> {
    try {
      log.log('[WordPressAdapter] Getting or creating tag:', tagName);

      const response = await fetch(`${this.baseUrl}/tag`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          integration_id: integrationId,
          name: tagName,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Failed to get or create tag');
      }

      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
      };
    } catch (error) {
      log.error('[WordPressAdapter] Error getting or creating tag:', error);
      throw error;
    }
  }

  /**
   * Test WordPress connection
   */
  async testConnection(integrationId: string): Promise<boolean> {
    try {
      log.log('[WordPressAdapter] Testing connection for integration:', integrationId);

      const response = await fetch(`${this.baseUrl}/test`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ integration_id: integrationId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Connection test failed');
      }

      const data = await response.json();
      log.log('[WordPressAdapter] Connection test successful');
      return data.connected === true;
    } catch (error) {
      log.error('[WordPressAdapter] Connection test failed:', error);
      throw error;
    }
  }
}

export const wordPressAdapter = new WordPressAdapter();

